import type { MapPlace } from './WeddingMap.types';

/** One pin's override from the map editor, as `eventMapPins` returns it. */
export type MapPinSetting = Readonly<{ pinId: string; hidden: boolean; note?: string | null }>;

/** The guest map's three layouts, as the editor's layout dropdown saves them on the map page. */
export const WEDDING_MAP_LAYOUTS = ['standard', 'editorial', 'immersive'] as const;
export type WeddingMapLayoutChoice = (typeof WEDDING_MAP_LAYOUTS)[number];

/** Longest note a pin can carry; `setEventMapPinSettings` rejects more. */
export const MAX_PIN_NOTE_LENGTH = 140;

/** Whether the map page carries a layout the couple saved, which `resolveMapLayout` then uses over the flags. */
export const isMapLayout = (value: string | null | undefined): value is WeddingMapLayoutChoice => WEDDING_MAP_LAYOUTS.some(layout => layout === value);

/**
 * Each place with the couple's edits from the map editor applied: whether they
 * turned its pin off, and their note (a blank one counts as none).
 *
 * Keyed by `MapPlace.id`, which is the pin id the editor saves under, so it
 * must stay stable for a place across loads (see `toScheduleMapPlaces`).
 *
 * Hidden places come back too, so the editor can list them and turn them back
 * on. The guest map drops them with `guestVisiblePlaces`.
 */
export const withPinSettings = (places: ReadonlyArray<MapPlace>, pins: ReadonlyArray<MapPinSetting> | null | undefined): ReadonlyArray<MapPlace> => {
  if (!pins || pins.length === 0) {
    return places;
  }
  const byPinId = new Map(pins.map(pin => [pin.pinId, pin]));
  return places.map(place => {
    const pin = byPinId.get(place.id);
    if (!pin) {
      return place;
    }
    const note = pin.note?.trim() || null;
    return { ...place, isHiddenByCouple: pin.hidden, note };
  });
};

/** The places a guest sees: every place the couple has not turned off. */
export const guestVisiblePlaces = (places: ReadonlyArray<MapPlace>): ReadonlyArray<MapPlace> =>
  places.some(place => place.isHiddenByCouple) ? places.filter(place => !place.isHiddenByCouple) : places;

/**
 * Which layout the guest map draws: the couple's saved `layoutType` when it
 * names one of the three, otherwise the flags, immersive ahead of editorial.
 */
export const resolveMapLayout = (savedLayout: string | null | undefined, flags: Readonly<{ isImmersiveLayout: boolean; isEditorialLayout: boolean }>): WeddingMapLayoutChoice => {
  if (isMapLayout(savedLayout)) {
    return savedLayout;
  }
  if (flags.isImmersiveLayout) {
    return 'immersive';
  }
  return flags.isEditorialLayout ? 'editorial' : 'standard';
};
